import { useCallback, useEffect, useRef, useState } from 'react';
import type { IceServerCfg } from './protocol';

export type PeerStatus =
  | { kind: 'idle' }
  | { kind: 'negotiating' }
  | { kind: 'checking' }
  | { kind: 'connected' }
  | { kind: 'disconnected'; reason: string }
  | { kind: 'failed'; reason: string };

export interface DataChannelHandles {
  input?: RTCDataChannel;
  control?: RTCDataChannel;
  files?: RTCDataChannel;
}

export interface UsePeerConnectionOpts {
  iceServers: IceServerCfg[] | null;
  onLocalIce: (candidate: RTCIceCandidate) => void;
  onTrack?: (track: MediaStreamTrack, streams: readonly MediaStream[]) => void;
  onDataChannel?: (channel: RTCDataChannel) => void;
}

export interface UsePeerConnectionApi {
  pcRef: React.MutableRefObject<RTCPeerConnection | null>;
  status: PeerStatus;
  /** Create a fresh PC; safe to call multiple times — replaces the previous one. */
  open: () => RTCPeerConnection;
  /** Drop the PC and free resources. */
  close: () => void;
  /** Apply a remote SDP answer (response to our initial `createOffer`). */
  applyRemoteAnswer: (sdp: string) => Promise<void>;
  /** Create + return an SDP offer; sets local description automatically. */
  createOffer: () => Promise<string>;
  /**
   * Phase A.2 — accept a host-initiated SDP offer (ICE restart) and return our answer SDP.
   * Used when the host periodically refreshes ICE credentials to outlast coturn's
   * `max-allocate-lifetime` cap. The PC stays the same — we just rotate the ICE ufrag/pwd and
   * candidates without dropping media tracks or DataChannels.
   */
  acceptRemoteOffer: (sdp: string) => Promise<string>;
  /** Push a remote ICE candidate. */
  addRemoteIce: (candidate: string, sdpMid: string | null, sdpMLineIndex: number | null) => Promise<void>;
}

/**
 * RTCPeerConnection lifecycle hook. The browser is always the *offerer* in our protocol — it builds an offer
 * with two `recvonly` media transceivers (video + audio) and three `negotiated` DataChannels (input, control, files).
 *
 * DataChannel IDs are pre-assigned (`negotiated: true`) to avoid an in-band negotiation roundtrip; the host opens
 * matching channels with the same IDs in str0m.
 */
export function usePeerConnection(opts: UsePeerConnectionOpts): UsePeerConnectionApi {
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const optsRef = useRef(opts);
  const [status, setStatus] = useState<PeerStatus>({ kind: 'idle' });

  useEffect(() => {
    optsRef.current = opts;
  }, [opts]);

  const open = useCallback((): RTCPeerConnection => {
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch {
        /* ignore */
      }
      pcRef.current = null;
    }
    const iceServers = optsRef.current.iceServers ?? [];
    const pc = new RTCPeerConnection({
      iceServers,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      iceTransportPolicy: 'all',
    });

    // Pre-allocate the 3 DataChannels we'll always use. Host side uses matching IDs.
    const inputDc = pc.createDataChannel('input', { id: 0, negotiated: true, ordered: true });
    const controlDc = pc.createDataChannel('control', { id: 1, negotiated: true, ordered: true });
    const filesDc = pc.createDataChannel('files', { id: 2, negotiated: true, ordered: true });
    optsRef.current.onDataChannel?.(inputDc);
    optsRef.current.onDataChannel?.(controlDc);
    optsRef.current.onDataChannel?.(filesDc);

    // We're the receiver of media — declare two `recvonly` transceivers so the SDP offer requests them.
    pc.addTransceiver('video', { direction: 'recvonly' });
    pc.addTransceiver('audio', { direction: 'recvonly' });

    pc.onicecandidate = (ev) => {
      if (ev.candidate) optsRef.current.onLocalIce(ev.candidate);
    };
    pc.ontrack = (ev) => {
      optsRef.current.onTrack?.(ev.track, ev.streams);
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      if (st === 'checking') setStatus({ kind: 'checking' });
      else if (st === 'connected' || st === 'completed') setStatus({ kind: 'connected' });
      else if (st === 'disconnected') setStatus({ kind: 'disconnected', reason: 'ice disconnected' });
      else if (st === 'failed') setStatus({ kind: 'failed', reason: 'ice failed' });
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === 'connected') setStatus({ kind: 'connected' });
      else if (st === 'disconnected') setStatus({ kind: 'disconnected', reason: 'peer disconnected' });
      else if (st === 'failed') setStatus({ kind: 'failed', reason: 'peer failed' });
      else if (st === 'closed') setStatus({ kind: 'disconnected', reason: 'peer closed' });
    };

    pcRef.current = pc;
    return pc;
  }, []);

  const close = useCallback(() => {
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch {
        /* ignore */
      }
      pcRef.current = null;
    }
    setStatus({ kind: 'idle' });
  }, []);

  useEffect(() => () => close(), [close]);

  const createOffer = useCallback(async (): Promise<string> => {
    const pc = pcRef.current;
    if (!pc) throw new Error('peer connection not open');
    setStatus({ kind: 'negotiating' });
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    if (!pc.localDescription) throw new Error('localDescription missing after setLocalDescription');
    return pc.localDescription.sdp;
  }, []);

  const applyRemoteAnswer = useCallback(async (sdp: string): Promise<void> => {
    const pc = pcRef.current;
    if (!pc) throw new Error('peer connection not open');
    await pc.setRemoteDescription({ type: 'answer', sdp });
  }, []);

  // Phase A.2 — host-initiated ICE restart inbound flow.
  // When the host periodically rotates ICE credentials (offer flips direction from peer→host
  // to host→peer), the browser must accept the new offer and create an answer. Critically the
  // RTCPeerConnection instance is REUSED — same media tracks, same DataChannels, same SCTP
  // association. Only the ICE ufrag/pwd and candidate set change.
  //
  // Error handling: setRemoteDescription/setLocalDescription can throw if the new offer is
  // structurally incompatible (e.g. m-line ordering changed) — that would indicate a host bug,
  // not a normal renegotiate. Caller logs + falls back to full session reset.
  const acceptRemoteOffer = useCallback(async (sdp: string): Promise<string> => {
    const pc = pcRef.current;
    if (!pc) throw new Error('peer connection not open');
    await pc.setRemoteDescription({ type: 'offer', sdp });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    if (!pc.localDescription) throw new Error('localDescription missing after setLocalDescription(answer)');
    return pc.localDescription.sdp;
  }, []);

  const addRemoteIce = useCallback(
    async (candidate: string, sdpMid: string | null, sdpMLineIndex: number | null): Promise<void> => {
      const pc = pcRef.current;
      if (!pc) return;
      try {
        await pc.addIceCandidate({ candidate, sdpMid: sdpMid ?? undefined, sdpMLineIndex: sdpMLineIndex ?? undefined });
      } catch (err) {
        // Spurious ICE candidates after negotiation are a known browser quirk — log but don't break the session.

        console.warn('addIceCandidate failed:', err);
      }
    },
    [],
  );

  return { pcRef, status, open, close, applyRemoteAnswer, createOffer, acceptRemoteOffer, addRemoteIce };
}
